import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { fetchProfile, updateProfile, uploadImage } from "../../services/user";
import toast from "react-hot-toast";

declare global {
  interface Window {
    // use any to avoid TypeScript errors when @types/google maps isn't installed
    google: any;
    __gmapsLoadingPromise?: Promise<void>;
  }
}

const GOOGLE_MAPS_API_KEY = "AIzaSyCNvTWfSe_R58Vm3cJqcGy5MDrtWhBVpcs";

/**
 * Promise-based loader for Google Maps JS (with Places)
 * Ensures only one script is injected and multiple callers wait on same promise.
 */
const loadGoogleMaps = (): Promise<void> => {
  if ((window as any).google && (window as any).google.maps) {
    return Promise.resolve();
  }
  if (window.__gmapsLoadingPromise) return window.__gmapsLoadingPromise;

  window.__gmapsLoadingPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(
      'script[src*="maps.googleapis.com"]'
    ) as HTMLScriptElement | null;
    if (existing) {
      // If script exists but google not ready yet, listen for load/error
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("Google Maps script failed to load"))
      );
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Google Maps script failed to load"));
    document.head.appendChild(script);
  });

  return window.__gmapsLoadingPromise;
};

interface AddressFields {
  street?: string;
  building?: string;
  locality?: string;
  city?: string;
  state?: string;
  area_code?: string;
  lat?: string;
  lng?: string;
}

interface ProfileData {
  email: string;
  name: string;
  phoneNumber: string;
  profilePath: string;
  businessName: string;
  gstin: string;
  storeDescription: string;
  bankAccountNumber: string;
  bankName: string;
  beneficiaryName: string;
  branchName: string;
  ifscCode?: string;
  supportCategory?: string[] | string;
  tag?: string;
  city: string;
  state: string;
  supportEmail: string;
  supportNumber: string;
  upiAddress: string;
  addressProofPath: string;
  eSignaturePath: string;
  panNumber: string;
  businessProfilePic: string;
  street?: string;
  building?: string;
  locality?: string;
  area_code?: string;
  lat?: string;
  lng?: string;
}

interface MapModalProps {
  initialLocation: { lat: number; lng: number } | null;
  onClose: () => void;
  onLocationSelect: (
    location: { lat: number; lng: number },
    address: string
  ) => void;
}

const MapModal: React.FC<MapModalProps> = ({
  initialLocation,
  onClose,
  onLocationSelect,
}) => {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(initialLocation);
  const [address, setAddress] = useState<string>("");

  useEffect(() => {
    let mounted = true;
    const reverseGeocode = (location: { lat: number; lng: number }) => {
      if (!(window as any).google) return;
      const geocoder = new (window as any).google.maps.Geocoder();
      geocoder.geocode({ location }, (results: any, status: string) => {
        if (status === "OK" && results?.[0]) {
          setAddress(results[0].formatted_address);
        } else {
          setAddress("");
        }
      });
    };

    const initMap = () => {
      if (!mapRef.current || !(window as any).google) return;

      const initialCenter = selectedLocation || { lat: 20.5937, lng: 78.9629 };
      const map = new (window as any).google.maps.Map(mapRef.current, {
        center: initialCenter,
        zoom: selectedLocation ? 15 : 5,
      });
      mapInstanceRef.current = map;

      if (selectedLocation) {
        markerRef.current = new (window as any).google.maps.Marker({
          position: selectedLocation,
          map,
          draggable: true,
        });
        reverseGeocode(selectedLocation);
        markerRef.current.addListener("dragend", (e: any) => {
          if (!e.latLng) return;
          const loc = { lat: e.latLng.lat(), lng: e.latLng.lng() };
          setSelectedLocation(loc);
          reverseGeocode(loc);
        });
      }

      // click on map to place marker
      map.addListener("click", (e: any) => {
        if (!e.latLng) return;
        const loc = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setSelectedLocation(loc);
        if (markerRef.current) {
          markerRef.current.setPosition(loc);
        } else {
          markerRef.current = new (window as any).google.maps.Marker({
            position: loc,
            map,
            draggable: true,
          });
          markerRef.current.addListener("dragend", (ev: any) => {
            if (!ev.latLng) return;
            const newLoc = { lat: ev.latLng.lat(), lng: ev.latLng.lng() };
            setSelectedLocation(newLoc);
            reverseGeocode(newLoc);
          });
        }
        reverseGeocode(loc);
      });
    };

    loadGoogleMaps()
      .then(() => {
        if (!mounted) return;
        initMap();
      })
      .catch((err) => {
        console.error("Google Maps load error (MapModal)", err);
        toast.error("Failed to load Google Maps");
      });

    return () => {
      mounted = false;
      // cleanup listeners and marker
      try {
        if ((window as any).google && markerRef.current) {
          (window as any).google.maps.event.clearInstanceListeners(
            markerRef.current
          );
          markerRef.current.setMap(null);
          markerRef.current = null;
        }
        if ((window as any).google && mapInstanceRef.current) {
          (window as any).google.maps.event.clearInstanceListeners(
            mapInstanceRef.current
          );
          mapInstanceRef.current = null;
        }
      } catch (e) {
        // ignore cleanup errors
      }
    };
    // initialLocation intentionally not in deps to initialize once per modal mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = () => {
    if (selectedLocation) {
      onLocationSelect(selectedLocation, address);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl h-[80vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Select Location</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            &times;
          </button>
        </div>

        <div className="flex-1 relative">
          <div ref={mapRef} className="w-full h-full rounded-md border"></div>
          <div className="absolute bottom-4 left-4 bg-white p-2 rounded shadow">
            <p className="text-sm">
              {selectedLocation
                ? `Selected: ${selectedLocation.lat.toFixed(
                    6
                  )}, ${selectedLocation.lng.toFixed(6)}`
                : "Click on the map to select a location"}
            </p>
            {address && <p className="text-sm mt-1">{address}</p>}
          </div>
        </div>

        <div className="mt-4 flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedLocation}
            className={`px-4 py-2 rounded text-white ${
              !selectedLocation
                ? "bg-blue-300"
                : "bg-blue-500 hover:bg-blue-600"
            }`}
          >
            Confirm Location
          </button>
        </div>
      </div>
    </div>
  );
};

const FormField: React.FC<{
  label: string;
  name: keyof ProfileData;
  type?: string;
  register: any;
  error: any;
  defaultValue?: string;
  disabled?: boolean;
  required?: boolean;
  isTextArea?: boolean;
  validation?: any;
}> = ({
  label,
  name,
  type = "text",
  register,
  error,
  defaultValue = "",
  disabled = false,
  required = false,
  isTextArea = false,
  validation = {},
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    {isTextArea ? (
      <textarea
        {...register(name, validation)}
        defaultValue={defaultValue}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
          error ? "border-red-500" : "border-gray-300"
        } ${disabled ? "bg-gray-100" : ""}`}
        rows={3}
      />
    ) : (
      <input
        type={type}
        {...register(name, {
          ...validation,
          onChange: (e: any) => {
            if (name === "gstin" || name === "panNumber" || name === "ifscCode") {
              e.target.value = e.target.value.toUpperCase();
            }
            if (validation.onChange) {
              validation.onChange(e);
            }
          },
        })}
        defaultValue={defaultValue}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
          name === "gstin" || name === "panNumber" || name === "ifscCode" ? "uppercase" : ""
        } ${
          error ? "border-red-500" : "border-gray-300"
        } ${disabled ? "bg-gray-100" : ""}`}
      />
    )}
    {error && <p className="mt-1 text-sm text-red-500">{error.message}</p>}
  </div>
);

const ProfileBusinessPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"profile" | "business">("profile");
  const [data, setData] = useState<ProfileData | null>(null);
  const [profileImage, setProfileImage] = useState<string>("");
  const [businessImage, setBusinessImage] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userId, setUserId] = useState<string>("");
  const [address, setAddress] = useState<AddressFields>({});
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const autocompleteRef = useRef<any | null>(null);
  const addressInputRef = useRef<HTMLInputElement | null>(null);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    getValues,
  } = useForm<ProfileData>();

  // fields allowed per tab (used for resetting / submitting)
  const ALLOWED_FIELDS_BY_TAB: Record<
    "profile" | "business",
    (keyof ProfileData)[]
  > = {
    profile: ["email", "name", "phoneNumber", "profilePath"],
    business: [
      "businessName",
      "gstin",
      "storeDescription",
      "building",
      "street",
      "locality",
      "area_code",
      "city",
      "state",
      "lat",
      "lng",
      "bankAccountNumber",
      "bankName",
      "beneficiaryName",
      "branchName",
      "ifscCode",
      "supportCategory",
      "tag",
      "supportEmail",
      "supportNumber",
      "upiAddress",
      "panNumber",
      "businessProfilePic",
      "addressProofPath",
      "eSignaturePath",
    ],
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const userId = localStorage.getItem("ud-user") ?? "";
        const response = await fetchProfile(userId);
        setUserId(userId);

        const rawData = response.data || {};
        let supportCategory = [];
        if (Array.isArray(rawData.supportCategory)) {
          supportCategory = rawData.supportCategory;
        } else if (typeof rawData.supportCategory === "string") {
          supportCategory = rawData.supportCategory ? rawData.supportCategory.split(",").map((s: string) => s.trim()) : [];
        }

        const profileData = {
          ...rawData,
          addressProofPath: rawData.addressProofPath || "",
          eSignaturePath: rawData.eSignaturePath || "",
          ifscCode: rawData.ifscCode || "",
          supportCategory,
          tag: rawData.tag || "",
        };

        setData(profileData);
        setProfileImage(profileData.profilePath || "");
        setBusinessImage(profileData.businessProfilePic || "");

        // Use reset to populate RHF default values reliably (prevents cross-field mixups)
        reset(profileData);

        const addressFields = {
          street: response.data.street || "",
          building: response.data.building || "",
          locality: response.data.locality || "",
          city: response.data.city || "",
          state: response.data.state || "",
          area_code: response.data.area_code || "",
          lat: response.data.lat || "",
          lng: response.data.lng || "",
        };

        setAddress(addressFields);

        if (response.data.lat && response.data.lng) {
          setSelectedLocation({
            lat: parseFloat(response.data.lat),
            lng: parseFloat(response.data.lng),
          });
        }
      } catch (err) {
        toast.error("Failed to fetch profile data");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [setValue, reset]);

  // Ensure form values are scoped to the active tab — prevents values from other tab showing up
  useEffect(() => {
    if (!data) return;

    const allowed = ALLOWED_FIELDS_BY_TAB[activeTab];
    const defaults: Partial<ProfileData> = {};

    allowed.forEach((key) => {
      // prefer top-level data, then address state
      if ((data as any)[key] !== undefined && (data as any)[key] !== null) {
        (defaults as any)[key] = (data as any)[key];
      } else if ((address as any)[key] !== undefined) {
        (defaults as any)[key] = (address as any)[key];
      } else {
        if (key === "supportCategory") {
          (defaults as any)[key] = [];
        } else {
          (defaults as any)[key] = "";
        }
      }
    });

    // include profilePath/businessProfilePic image values so disabled image-based UI shows correct image
    if (activeTab === "profile") {
      (defaults as any).profilePath = profileImage || data.profilePath || "";
    } else {
      (defaults as any).businessProfilePic =
        businessImage || data.businessProfilePic || "";
    }

    reset(defaults as any);

    // if business tab, make sure the address input shows current street (autocomplete input)
    if (activeTab === "business" && addressInputRef.current) {
      addressInputRef.current.value = address.street || data.street || "";
    }
    // when switching to profile, ensure disabled email shows current email
    if (activeTab === "profile" && addressInputRef.current) {
      // clear address input when on profile
      addressInputRef.current.value = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, data, profileImage, businessImage, address.street]);

  useEffect(() => {
    let mounted = true;

    const initializeAutocomplete = () => {
      if (
        !(window as any).google ||
        !(addressInputRef.current instanceof HTMLInputElement) ||
        autocompleteRef.current
      )
        return;

      autocompleteRef.current = new (
        window as any
      ).google.maps.places.Autocomplete(addressInputRef.current, {
        types: ["address"],
        componentRestrictions: { country: "in" },
        fields: ["address_components", "geometry"],
      });

      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current?.getPlace();
        if (!place?.geometry || !place?.address_components) return;

        const newAddress: AddressFields = {};

        place.address_components.forEach((component: any) => {
          const type = component.types[0];
          switch (type) {
            case "street_number":
              newAddress.street = component.long_name;
              break;
            case "route":
              newAddress.street = `${newAddress.street ?? ""} ${
                component.long_name
              }`.trim();
              break;
            case "sublocality_level_1":
            case "sublocality":
              newAddress.locality = component.long_name;
              break;
            case "locality":
              newAddress.city = component.long_name;
              break;
            case "administrative_area_level_1":
              newAddress.state = component.long_name;
              break;
            case "postal_code":
              newAddress.area_code = component.long_name;
              break;
            case "premise":
              newAddress.building = component.long_name;
              break;
          }
        });

        newAddress.lat = place.geometry.location?.lat()?.toString() || "";
        newAddress.lng = place.geometry.location?.lng()?.toString() || "";

        setAddress(newAddress);
        setSelectedLocation({
          lat: parseFloat(newAddress.lat || "0"),
          lng: parseFloat(newAddress.lng || "0"),
        });
        Object.entries(newAddress).forEach(([key, value]) =>
          setValue(key as keyof ProfileData, value || "")
        );
      });
    };

    const loadAndInit = async () => {
      if (!mounted) return;
      if (activeTab !== "business" || !addressInputRef.current) return;
      try {
        await loadGoogleMaps();
        initializeAutocomplete();
      } catch (err) {
        console.error("Failed to load Google Maps for autocomplete", err);
        toast.error("Failed to load Google Maps (autocomplete)");
      }
    };

    loadAndInit();

    return () => {
      mounted = false;
      try {
        if (autocompleteRef.current && (window as any).google) {
          (window as any).google.maps.event.clearInstanceListeners(
            autocompleteRef.current
          );
          autocompleteRef.current = null;
        }
      } catch (e) {
        // ignore
      }
    };
    // only re-run when activeTab changes
  }, [activeTab, setValue]);

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    setImage: React.Dispatch<React.SetStateAction<string>>
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      const form = new FormData();
      form.append("file", file);
      try {
        const response = await uploadImage(form);
        if (response.success) {
          setImage(response.data);
          toast.success("Image uploaded successfully!");
        } else {
          toast.error("Failed to upload image");
        }
      } catch (error) {
        toast.error("Error uploading image");
        console.error(error);
      }
    }
  };

  const handleMapLocationSelect = (
    location: { lat: number; lng: number },
    addressStr: string
  ) => {
    setSelectedLocation(location);
    setValue("lat", location.lat.toString());
    setValue("lng", location.lng.toString());
    setValue("street", addressStr);
    setShowMapModal(false);
    setAddress((prev) => ({
      ...prev,
      street: addressStr,
      lat: location.lat.toString(),
      lng: location.lng.toString(),
    }));
    if (addressInputRef.current) addressInputRef.current.value = addressStr;
  };

  const onSubmit = async (formData: ProfileData) => {
    try {
      setIsSubmitting(true);

      // Normalize some fields
      if (formData.gstin) formData.gstin = formData.gstin.toUpperCase();
      if (formData.panNumber) formData.panNumber = formData.panNumber.toUpperCase();
      if (formData.ifscCode) formData.ifscCode = formData.ifscCode.toUpperCase();
      if (formData.upiAddress) formData.upiAddress = formData.upiAddress.toLowerCase();

      // Basic client-side required checks for images
      if (activeTab === "profile" && !profileImage) {
        toast.error("Profile image is required");
        setIsSubmitting(false);
        return;
      }
      if (activeTab === "business" && !businessImage) {
        toast.error("Business image is required");
        setIsSubmitting(false);
        return;
      }

      const allowed = ALLOWED_FIELDS_BY_TAB[activeTab];

      const payload: Partial<ProfileData & { userId?: string }> = { userId };

      // Include image overrides only if present
      if (profileImage) payload.profilePath = profileImage;
      if (businessImage) payload.businessProfilePic = businessImage;

      // Copy only allowed fields from formData
      allowed.forEach((key) => {
        const value = (formData as any)[key];
        if (
          value !== undefined &&
          value !== null &&
          (value !== "" || key === "addressProofPath" || key === "eSignaturePath")
        ) {
          // @ts-ignore
          payload[key] = value;
        }
      });

      // Ensure lat/lng reflect map selection if present
      if (selectedLocation) {
        payload.lat = selectedLocation.lat.toString();
        payload.lng = selectedLocation.lng.toString();
      } else {
        if ((formData as any).lat) payload.lat = (formData as any).lat;
        if ((formData as any).lng) payload.lng = (formData as any).lng;
      }

      // final required check (server-ready)
      const missing = (allowed as string[]).filter(
        (k) =>
          k !== "addressProofPath" &&
          k !== "eSignaturePath" &&
          (!(payload as any)[k] || (payload as any)[k] === "")
      );
      if (missing.length > 0) {
        toast.error(`Required fields missing: ${missing.join(", ")}`);
        setIsSubmitting(false);
        return;
      }
      // email is not editable, so never send it
      payload.email = undefined;
      const response = await updateProfile(payload);

      if (response.success) {
        toast.success("Profile updated successfully");
        setData(response.data);
      } else {
        toast.error(response.message || "Failed to update profile");
      }
    } catch (error) {
      toast.error("Failed to update profile");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin h-12 w-12 border-4 border-green-500 border-t-transparent rounded-full"></div>
      </div>
    );
  if (!data)
    return (
      <div className="text-center py-10 text-red-500">
        Failed to load profile data
      </div>
    );

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="bg-white shadow rounded">
        <div className="flex border-b">
          {["profile", "business"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as "profile" | "business")}
              className={`flex-1 text-center py-3 font-medium ${
                activeTab === tab
                  ? "text-green-600 border-b-2 border-green-600"
                  : "text-gray-500"
              }`}
            >
              {tab === "profile" ? "Profile" : "Business"}
            </button>
          ))}
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <div className="flex items-center gap-6">
            <div className="relative group">
              <img
                src={
                  (activeTab === "profile" ? profileImage : businessImage) ||
                  "/default-profile.png"
                }
                className="w-24 h-24 rounded-full object-cover"
              />
              <label className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer">
                <span className="text-white text-sm">Change</span>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) =>
                    handleImageUpload(
                      e,
                      activeTab === "profile"
                        ? setProfileImage
                        : setBusinessImage
                    )
                  }
                />
              </label>
            </div>
            <div>
              <h3 className="text-lg font-semibold">
                {activeTab === "profile" ? "Profile" : "Business"} Information
              </h3>
              <p className="text-sm text-gray-500">
                Update your {activeTab} details
              </p>
            </div>
          </div>

          {activeTab === "profile" ? (
            <>
              <FormField
                label="Email"
                name="email"
                type="email"
                register={register}
                error={errors.email}
                defaultValue={data.email}
                disabled
                validation={{
                  required: "Email is required",
                  pattern: { value: /^\S+@\S+\.\S+$/, message: "Invalid email" },
                }}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Name"
                  name="name"
                  register={register}
                  error={errors.name}
                  defaultValue={data.name}
                  required
                  validation={{ required: "Name is required" }}
                />
                <FormField
                  label="Phone Number"
                  name="phoneNumber"
                  register={register}
                  error={errors.phoneNumber}
                  defaultValue={data.phoneNumber}
                  required
                  validation={{
                    required: "Phone number is required",
                    pattern: {
                      value: /^[6-9]\d{9}$/,
                      message: "Invalid Indian phone number (10 digits starting 6-9)",
                    },
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <FormField
                label="Business Name"
                name="businessName"
                register={register}
                error={errors.businessName}
                defaultValue={data.businessName}
                required
                validation={{ required: "Business name is required" }}
              />
              <FormField
                label="GSTIN"
                name="gstin"
                register={register}
                error={errors.gstin}
                defaultValue={data.gstin}
                required
                validation={{
                  required: "GSTIN is required",
                  validate: (v: string) => {
                    if (!v) return "GSTIN is required";
                    const val = v.toUpperCase();
                    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][A-Z0-9]Z[A-Z0-9]$/i;
                    if (!gstRegex.test(val)) return "Invalid GSTIN format";
                    return true;
                  },
                }}
              />
              <FormField
                label="Store Description"
                name="storeDescription"
                register={register}
                error={errors.storeDescription}
                defaultValue={data.storeDescription}
                isTextArea
                validation={{
                  required: "Store description is required",
                  maxLength: { value: 1000, message: "Too long" },
                }}
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Support Category <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {["Grocery", "Home & Kitchen", "Fashion"].map((category) => (
                    <label
                      key={category}
                      className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 focus-within:ring-2 focus-within:ring-green-500"
                    >
                      <input
                        type="checkbox"
                        value={category}
                        {...register("supportCategory", {
                          required: "At least one support category must be selected",
                        })}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        {category}
                      </span>
                    </label>
                  ))}
                </div>
                {errors.supportCategory && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.supportCategory.message as string}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Social Sector Establishment Type <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("tag", { required: "Social Sector Establishment Type is required" })}
                  defaultValue={data.tag}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    errors.tag ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select an establishment type</option>
                  {["SHG", "FPO", "Food Processing Micro/Nano Enterprise", "Women Owned Business"].map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {errors.tag && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.tag.message as string}
                  </p>
                )}
              </div>

              <div className="mb-4">
                <button
                  type="button"
                  onClick={() => setShowMapModal(true)}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Select Location on Map
                </button>
                {selectedLocation && (
                  <p className="mt-2 text-sm text-gray-600">
                    Selected location: {selectedLocation.lat.toFixed(6)},{" "}
                    {selectedLocation.lng.toFixed(6)}
                  </p>
                )}
              </div>

              <input
                type="text"
                ref={addressInputRef}
                placeholder="Start typing address..."
                className="border p-2 rounded w-full"
              />
              <FormField
                label="Building/Flat No."
                name="building"
                register={register}
                error={errors.building}
                defaultValue={address.building}
                validation={{
                  required: "Building/Flat No. is required",
                  maxLength: { value: 100, message: "Too long" },
                }}
              />
              <FormField
                label="Street"
                name="street"
                register={register}
                error={errors.street}
                defaultValue={address.street}
                validation={{
                  required: "Street is required",
                  maxLength: { value: 200, message: "Too long" },
                }}
              />
              <FormField
                label="Locality/Area"
                name="locality"
                register={register}
                error={errors.locality}
                defaultValue={address.locality}
                validation={{
                  required: "Locality/Area is required",
                  maxLength: { value: 100, message: "Too long" },
                }}
              />
              <FormField
                label="Pincode"
                name="area_code"
                register={register}
                error={errors.area_code}
                defaultValue={address.area_code}
                validation={{
                  required: "Pincode is required",
                  pattern: { value: /^\d{6}$/, message: "Pincode must be 6 digits" },
                }}
              />
              <FormField
                label="City"
                name="city"
                register={register}
                error={errors.city}
                defaultValue={address.city}
                validation={{ required: "City is required" }}
              />
              <FormField
                label="State"
                name="state"
                register={register}
                error={errors.state}
                defaultValue={address.state}
                validation={{ required: "State is required" }}
              />
              <input type="hidden" {...register("lat", { required: "Latitude required" })} />
              <input type="hidden" {...register("lng", { required: "Longitude required" })} />

              <FormField
                label="Bank Account Number"
                name="bankAccountNumber"
                register={register}
                error={errors.bankAccountNumber}
                defaultValue={data.bankAccountNumber}
                required
                validation={{
                  required: "Bank account number is required",
                  pattern: {
                    value: /^[0-9]{6,30}$/,
                    message: "Bank account must be numeric (6-30 digits)",
                  },
                }}
              />
              <FormField
                label="Bank Name"
                name="bankName"
                register={register}
                error={errors.bankName}
                defaultValue={data.bankName}
                required
                validation={{ required: "Bank name is required" }}
              />
              <FormField
                label="Beneficiary Name"
                name="beneficiaryName"
                register={register}
                error={errors.beneficiaryName}
                defaultValue={data.beneficiaryName}
                required
                validation={{ required: "Beneficiary name is required" }}
              />
              <FormField
                label="Branch Name"
                name="branchName"
                register={register}
                error={errors.branchName}
                defaultValue={data.branchName}
                required
                validation={{ required: "Branch name is required" }}
              />
              <FormField
                label="IFSC Code"
                name="ifscCode"
                register={register}
                error={errors.ifscCode}
                defaultValue={data.ifscCode}
                required
                validation={{
                  required: "IFSC Code is required",
                  validate: (v: string) => {
                    if (!v) return "IFSC Code is required";
                    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/i;
                    if (!ifscRegex.test(v)) return "Invalid IFSC Code format (e.g., SBIN0001234)";
                    return true;
                  },
                }}
              />
              <FormField
                label="Support Email"
                name="supportEmail"
                type="email"
                register={register}
                error={errors.supportEmail}
                defaultValue={data.supportEmail}
                required
                validation={{
                  required: "Support email is required",
                  pattern: {
                    value: /^\S+@\S+\.\S+$/,
                    message: "Invalid email",
                  },
                }}
              />
              <FormField
                label="Support Number"
                name="supportNumber"
                register={register}
                error={errors.supportNumber}
                defaultValue={data.supportNumber}
                required
                validation={{
                  required: "Support number is required",
                  pattern: {
                    value: /^[6-9]\d{9}$/,
                    message: "Invalid phone number",
                  },
                }}
              />
              <FormField
                label="UPI Address"
                name="upiAddress"
                register={register}
                error={errors.upiAddress}
                defaultValue={data.upiAddress}
                required
                validation={{
                  required: "UPI address is required",
                  validate: (v: string) => {
                    if (!v) return "UPI address is required";
                    const upiRegex = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/i;
                    return upiRegex.test(v) || "Invalid UPI address (example: name@bank)";
                  },
                }}
              />
              <FormField
                label="PAN Number"
                name="panNumber"
                register={register}
                error={errors.panNumber}
                defaultValue={data.panNumber}
                required
                validation={{
                  required: "PAN is required",
                  validate: (v: string) => {
                    if (!v) return "PAN is required";
                    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/i;
                    if (!panRegex.test(v)) return "Invalid PAN format";
                    return true;
                  },
                }}
              />
            </>
          )}

          <div className="mt-8">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 px-4 rounded-lg font-medium text-white ${
                isSubmitting
                  ? "bg-green-400"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>

      {showMapModal && (
        <MapModal
          initialLocation={selectedLocation}
          onClose={() => setShowMapModal(false)}
          onLocationSelect={handleMapLocationSelect}
        />
      )}
    </div>
  );
};

export default ProfileBusinessPage;
